import { Dispatch } from 'redux';
import { ENTITIES } from '../constants/entityConsts';
import fileHandler from './FileHandler';
import channelHandler from './ChannelHandler';
import invoiceHandler from './InvoiceHandler';
import paymentHandler from './paymentHandler';
import InternalUserHandler from './InternalUserHandler';
import clientHandler from './ClientHandler';
import companyHandler from './CompanyHandler';
import { FileNotificationsHandler } from './NotificationsHandler';
import { NotificationGroups } from '../store/notifications/types';
import { FlagContextType, PortalConfig } from '../context';
import FormRequestHandler from './FormRequestHandler';
import formRequestNotificationHandler from './FormRequestNotificationHandler';
import formCompletedNotificationHandler from './FormCompletedNotificationHandler';
import FormTemplatesHandler from './FormTemplateHandler';
import OnBoardingTasksHandler from './OnBoardingTasksHandler';
import subscriptionHandler from './SubscriptionHandler';
import newInvoiceNotificationHandler from './NewInvoiceNotificationHandler';
import PortalSettingsHandler from './PortalSettingsHandler';
import CustomFieldsHandler from './CustomFieldsHandler';
import { MessageSettingsHandler } from './MessageSettingsHandler';
import ExtensionItemHandler from './ExtensionItemHandler';
import TagHandler from './TagHandler';
import ArticleHandler from './ArticleHandler';
import KnowledgeBaseSettingsHandler from './KnowledgeBaseSettingsHandler';
import { WebSocketMutations } from '../hooks/useWebSocketMutations';
import { insertInboxNotifications } from './inboxNotificationHandler';
import { FormsV2ResponseHandler } from './FormsV2ResponseHandler';
import FormsTemplatesV2Handler from './formsTemplatesV2Handler';

export interface MsgEvent {
  entity: string;
  event: string;
  items: Array<any>;
}

export const wsMessageHandler = (
  msgEvent: MsgEvent,
  portalConfig: PortalConfig,
  userID: string,
  webSocketMutations: WebSocketMutations,
  flags: FlagContextType,
  dispatch: Dispatch<any>,
) => {
  const { entity, event, items } = msgEvent;
  // forms response ws item entity string
  // is a combination of the entity name and the formResponseId
  // so we need to check if the entity string includes the form response
  // entity name.
  if (entity.includes(ENTITIES.FORM_RESPONSE)) {
    return FormsV2ResponseHandler(event, items, dispatch);
  }
  switch (entity) {
    case ENTITIES.FILE_METADATA:
      return fileHandler(event, items);
    case ENTITIES.STRIPE_INVOICE:
      return invoiceHandler(event, items);
    case ENTITIES.STRIPE_SUBSCRIPTION:
      return subscriptionHandler(event, items);
    case ENTITIES.FILE_CHANNEL:
      return channelHandler(event, items);
    case ENTITIES.CLIENT_USER:
      return clientHandler(event, items, portalConfig, userID);
    case ENTITIES.INTERNAL_USER:
      return InternalUserHandler(event, items, portalConfig, userID);
    case ENTITIES.CLIENT_COMPANY:
      return companyHandler(event, items);
    case ENTITIES.PAYMENT_INFO:
      return paymentHandler(event, items);
    case ENTITIES.INTAKE_FORM_RESPONSE:
      return FormRequestHandler(event, items);
    case ENTITIES.NOTIFY_NEW_INVOICE:
      return newInvoiceNotificationHandler(event, items);
    case ENTITIES.NOTIFY_FILE_UPLOADED:
      return FileNotificationsHandler(
        NotificationGroups.FileNotifications,
        event,
        items,
        // allow marking notification as read only when
        // a. user is a client
        // b. files notification center flag in off
        portalConfig.viewMode === 'client' ||
          !flags.EnableFilesNotificationCenter,
      );
    case ENTITIES.NOTIFY_FILE_ESIGN_REQUEST:
      return FileNotificationsHandler(
        NotificationGroups.EsigRequestNotifications,
        event,
        items,
      );
    case ENTITIES.NOTIFY_FILE_ESIGN_REQUEST_COMPLETE:
      FileNotificationsHandler(
        NotificationGroups.EsigRequestCompleteNotifications,
        event,
        items,
      );
      return insertInboxNotifications(items);
    case ENTITIES.NOTIFY_FORM_REQUEST:
      return formRequestNotificationHandler(event, items);
    case ENTITIES.NOTIFY_FORM_COMPLETE:
      return formCompletedNotificationHandler(event, items);
    case ENTITIES.INTAKE_FORM_TEMPLATE:
      return FormTemplatesHandler(event, items);
    case ENTITIES.FORM_TEMPLATE:
      return FormsTemplatesV2Handler(event, items);
    case ENTITIES.ONBOARDING_TASK:
      // as onboarding task gets update socket event every time for onboarding tasks (bank connected, team member added etc...)
      // so we can safely invalidate cache for recommended resources too, so that refreshed data is fetched for them when user go to home page
      webSocketMutations.invalidateRecommendedResources();
      return OnBoardingTasksHandler(event, items);
    case ENTITIES.DOMAIN_SETTINGS:
      // invalidate cache for recommended resources on domain connected event, so that refreshed data is fetched when user user go to home page
      webSocketMutations.invalidateRecommendedResources();
      webSocketMutations.setVerifiedDomains(items);
      break;
    case ENTITIES.REFERRAL:
      // update the cache for referral
      webSocketMutations.invalidateReferrals();
      break;
    case ENTITIES.PORTAL_SETTINGS:
      return PortalSettingsHandler(event, items);
    case ENTITIES.MESSAGE_SETTINGS:
      return MessageSettingsHandler(event, items);
    case ENTITIES.CLIENT_CUSTOM_FIELDS:
      return CustomFieldsHandler(event, items);
    case ENTITIES.EXTENSION_ITEM:
      return ExtensionItemHandler(event, items);
    case ENTITIES.TAG:
      return TagHandler(event, items);
    case ENTITIES.ARTICLE:
      return ArticleHandler(event, items);
    case ENTITIES.KNOWLEDGE_BASE_SETTINGS:
      return KnowledgeBaseSettingsHandler(event, items);
    case ENTITIES.PORTAL_CONFIG:
      if (portalConfig.updatePortalConfig) {
        // portal config by default does not have update method
        // this gets initialized in App.tsx and passed to websocket handler
        portalConfig.updatePortalConfig(items[0].fields);
      }
      break;
    case ENTITIES.NOTIFICATION_GROUPING:
      return insertInboxNotifications(items);
    default:
      break;
  }
  return null;
};
